// src/pages/BookDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import AddEntryModal from "../components/AddEntryModal";
import AddMemberModal from "../components/AddMemberModal";
import { useAuth } from "../context/AuthContext";
import "./BookDetail.css";
import RenameBookModal from "../components/RenameBookModal";
import ConfirmModal from "../components/ConfirmModal";

const BookDetail = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // main states
  const [book, setBook] = useState(null);
  const [entries, setEntries] = useState([]);

  // filters
  const [filterType, setFilterType] = useState("ALL");
  const [sortAmount, setSortAmount] = useState("DEFAULT");
  const [memberFilter, setMemberFilter] = useState("ALL");
  const [paymentFilter, setPaymentFilter] = useState("ALL");
  const [dateFilter, setDateFilter] = useState({ start: "", end: "" });

  // modals
  const [showEntryModal, setShowEntryModal] = useState(null);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  /* FETCH BOOK */
  const fetchBook = async () => {
    try {
      const res = await api.get(`/books/${bookId}`);
      setBook(res.data.book);
      setEntries(res.data.entries || []);
    } catch {
      alert("Error loading book");
    }
  };

  useEffect(() => {
    fetchBook();
  }, [bookId]);

  /* ROLES */
  const currentRole = (() => {
    if (!book || !user) return null;
    if (book.owner === user.id || book.owner === user._id) return "owner";

    const member = book.members?.find(
      (m) => m.user?._id === user.id || m.user?._id === user._id
    );
    return member?.role || null;
  })();

  const canManageMembers = currentRole === "owner";
  const canManageRoles = currentRole === "owner";
  const canRenameBook = currentRole === "owner";
  const canDeleteBook = currentRole === "owner";
  const canAddEntry = ["owner", "editor"].includes(currentRole);

  /* FILTER + SORT */
  const filteredSortedEntries = useMemo(() => {
    let list = [...entries];

    if (filterType === "IN") list = list.filter((e) => e.type === "IN");
    if (filterType === "OUT") list = list.filter((e) => e.type === "OUT");

    if (memberFilter !== "ALL")
      list = list.filter((e) => (e.createdByName || "Unknown") === memberFilter);

    if (paymentFilter !== "ALL")
      list = list.filter((e) => e.paymentBy === paymentFilter);

    if (dateFilter.start)
      list = list.filter((e) => new Date(e.date) >= new Date(dateFilter.start));
    if (dateFilter.end)
      list = list.filter((e) => new Date(e.date) <= new Date(dateFilter.end));

    list.sort((a, b) => {
      if (sortAmount === "DEFAULT") return new Date(b.date) - new Date(a.date);
      if (sortAmount === "ASC") return Number(a.amount) - Number(b.amount);
      if (sortAmount === "DESC") return Number(b.amount) - Number(a.amount);
      return 0;
    });

    return list;
  }, [entries, filterType, memberFilter, paymentFilter, dateFilter, sortAmount]);

  /* TOTALS */
  const totals = useMemo(() => {
    let cashIn = 0,
      cashOut = 0;
    filteredSortedEntries.forEach((e) => {
      e.type === "IN" ? (cashIn += Number(e.amount)) : (cashOut += Number(e.amount));
    });
    return { cashIn, cashOut, balance: cashIn - cashOut };
  }, [filteredSortedEntries]);

  /* UNIQUE FILTER VALUES */
  const uniqueMembers = useMemo(
    () => [...new Set(entries.map((e) => e.createdByName || "Unknown"))],
    [entries]
  );

  const uniquePayments = useMemo(
    () => [...new Set(entries.map((e) => e.paymentBy).filter(Boolean))],
    [entries]
  );

  /* DATE PRESET HELPERS */
  const isoToday = () => new Date().toISOString().slice(0, 10);
  const isoDaysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
  };
  const isoFirstOfMonth = () => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10);
  };

  /* Detect if chip is active */
  const isPresetActive = (preset) => {
    const { start, end } = dateFilter;
    if (!start || !end) return false;

    if (preset === "today") return start === isoToday() && end === isoToday();
    if (preset === "yesterday") {
      const y = isoDaysAgo(1);
      return start === y && end === y;
    }
    if (preset === "7") return start === isoDaysAgo(7) && end === isoToday();
    if (preset === "30") return start === isoDaysAgo(30) && end === isoToday();
    if (preset === "month") return start === isoFirstOfMonth() && end === isoToday();

    return false;
  };

  /* Apply preset OR toggle off */
  const togglePreset = (preset) => {
    if (isPresetActive(preset)) {
      setDateFilter({ start: "", end: "" });
      return;
    }

    const today = isoToday();
    let start = today;
    let end = today;

    if (preset === "yesterday") start = end = isoDaysAgo(1);
    if (preset === "7") start = isoDaysAgo(7);
    if (preset === "30") start = isoDaysAgo(30);
    if (preset === "month") start = isoFirstOfMonth();

    setDateFilter({ start, end });
  };

  /* CREATE ENTRY */
  const handleCreateEntry = async (data) => {
    try {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => v !== null && fd.append(k, v));
      const res = await api.post(`/books/${bookId}/entries`, fd);
      setEntries((p) => [res.data.entry, ...p]);
      setShowEntryModal(null);
    } catch {
      alert("Error creating entry");
    }
  };

  /* ADD MEMBER */
  const handleAddMember = async ({ mobile }) => {
    try {
      const res = await api.post(`/books/${bookId}/members`, { mobile });
      const { inviteLink, bookName, invitedBy } = res.data;

      const shareText = `You've been invited to join the cash book "${bookName}" by ${invitedBy}.\n\nClick to join:\n${inviteLink}`;

      setShowMemberModal(false);
      fetchBook();

      if (navigator.share) {
        await navigator.share({
          title: "CashBook Invitation",
          text: shareText,
          url: inviteLink,
        });
        return;
      }

      window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`);
    } catch {
      alert("Error adding member");
    }
  };

  /* RENAME / EXPORT / DELETE */
  const handleRename = async (newName) => {
    try {
      const res = await api.patch(`/books/${bookId}`, { name: newName });
      setBook(res.data.book);
      setShowRenameModal(false);
    } catch {
      alert("Error renaming");
    }
  };

  const handleExport = (format) => {
    const token = localStorage.getItem("token");
    if (!token) return alert("Please login");
    window.open(
      `${process.env.REACT_APP_API_URL}/books/${bookId}/export/${format}?token=${token}`,
      "_blank"
    );
  };

  const handleDeleteBook = async () => {
    try {
      await api.delete(`/books/${bookId}`);
      navigate("/");
    } catch {
      alert("Error deleting book");
    }
  };

  if (!book) return <div className="bd-loading">Loading...</div>;

  /* ----------------------- UI ------------------------ */
  return (
    <div className="book-detail-page">
      {/* HEADER */}
      <header className="bd-header">
        <button className="bd-back-btn" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left"></i>
        </button>

        <h1 className="bd-title">{book.name}</h1>

        <div className="bd-header-actions bd-menu-wrapper">
          {canManageMembers && (
            <button className="bd-icon-btn" onClick={() => setShowMemberModal(true)}>
              <i className="bi bi-person-plus-fill"></i>
            </button>
          )}

          <button className="bd-icon-btn" onClick={() => setSettingsOpen((v) => !v)}>
            <i className="bi bi-three-dots-vertical"></i>
          </button>

          {settingsOpen && (
            <div className="bd-menu">
              {canRenameBook && (
                <button
                  onClick={() => {
                    setShowRenameModal(true);
                    setSettingsOpen(false);
                  }}
                >
                  Rename Book
                </button>
              )}

              <button onClick={() => handleExport("excel")}>Export Excel</button>
              <button onClick={() => handleExport("pdf")}>Export PDF</button>

              {canManageRoles && (
                <button
                  onClick={() => {
                    navigate(`/books/${bookId}/roles`);
                    setSettingsOpen(false);
                  }}
                >
                  Manage Roles
                </button>
              )}

              {canDeleteBook && (
                <button
                  className="danger"
                  onClick={() => {
                    setShowDeleteConfirm(true);
                    setSettingsOpen(false);
                  }}
                >
                  Delete Book
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {/* SUMMARY */}
      <div className="bd-summary">
        <div
          className={`bd-card bd-full ${
            totals.balance > 0 ? "bd-green" : totals.balance < 0 ? "bd-red" : ""
          }`}
        >
          <div className="bd-label">Balance</div>
          <div
            className={`bd-value ${
              totals.balance > 0 ? "green-text" : totals.balance < 0 ? "red-text" : ""
            }`}
          >
            ₹{totals.balance}
          </div>
        </div>

        <div className="bd-row">
          <div className="bd-card bd-green">
            <div className="bd-label">Cash In</div>
            <div className="bd-value green-text">+₹{totals.cashIn}</div>
          </div>

          <div className="bd-card bd-red">
            <div className="bd-label">Cash Out</div>
            <div className="bd-value red-text">-₹{totals.cashOut}</div>
          </div>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="bd-filter-scroll">
        <div className="bd-filter-row">

          {/* Type */}
          <button className={`bd-filter-item ${filterType === "ALL" ? "active" : ""}`} onClick={() => setFilterType("ALL")}>All</button>
          <button className={`bd-filter-item ${filterType === "IN" ? "active" : ""}`} onClick={() => setFilterType("IN")}>Cash In</button>
          <button className={`bd-filter-item ${filterType === "OUT" ? "active" : ""}`} onClick={() => setFilterType("OUT")}>Cash Out</button>

          {/* Sort */}
          <select
            className={`bd-filter-item select ${sortAmount !== "DEFAULT" ? "active" : ""}`}
            value={sortAmount}
            onChange={(e) => setSortAmount(e.target.value)}
          >
            <option value="DEFAULT">Latest First</option>
            <option value="DESC">High → Low</option>
            <option value="ASC">Low → High</option>
          </select>

          {/* Member */}
          <select
            className="bd-filter-item select"
            value={memberFilter}
            onChange={(e) => setMemberFilter(e.target.value)}
          >
            <option value="ALL">All Members</option>
            {uniqueMembers.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>

          {/* Payment */}
          <select
            className="bd-filter-item select"
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
          >
            <option value="ALL">All Payments</option>
            {uniquePayments.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          {/* Date Chips */}
          <button className={`bd-filter-item chip ${isPresetActive("today") ? "active" : ""}`} onClick={() => togglePreset("today")}>Today</button>

          <button className={`bd-filter-item chip ${isPresetActive("yesterday") ? "active" : ""}`} onClick={() => togglePreset("yesterday")}>Yesterday</button>

          <button className={`bd-filter-item chip ${isPresetActive("7") ? "active" : ""}`} onClick={() => togglePreset("7")}>Last 7 Days</button>

          <button className={`bd-filter-item chip ${isPresetActive("30") ? "active" : ""}`} onClick={() => togglePreset("30")}>Last 30 Days</button>

          <button className={`bd-filter-item chip ${isPresetActive("month") ? "active" : ""}`} onClick={() => togglePreset("month")}>This Month</button>

          {/* Custom date */}
          <input
            type="date"
            className="bd-filter-item date"
            value={dateFilter.start}
            onChange={(e) => setDateFilter((d) => ({ ...d, start: e.target.value }))}
          />
          <input
            type="date"
            className="bd-filter-item date"
            value={dateFilter.end}
            onChange={(e) => setDateFilter((d) => ({ ...d, end: e.target.value }))}
          />
        </div>
      </div>

      {/* ENTRIES */}
      <div className="bd-entries">
        {filteredSortedEntries.length === 0 ? (
          <div className="bd-empty">No entries found</div>
        ) : (
          filteredSortedEntries.map((entry) => (
            <div
              key={entry._id}
              className={`bd-entry-card ${entry.type === "IN" ? "in" : "out"}`}
              onClick={() => navigate(`/entries/${entry._id}`)}
            >
              <div className="bd-entry-main">
                <span className={`bd-entry-amount ${entry.type === "IN" ? "amount-in" : "amount-out"}`}>
                  {entry.type === "IN" ? "+₹" : "-₹"}
                  {entry.amount}
                </span>
                <span className="bd-entry-type">{entry.type === "IN" ? "Cash In" : "Cash Out"}</span>
              </div>

              <div className="bd-entry-meta">
                <span>
                  <i className="bi bi-calendar-event meta-icon"></i>{" "}
                  {new Date(entry.date).toLocaleDateString()}
                </span>
                <span>
                  <i className="bi bi-credit-card meta-icon"></i> {entry.paymentBy || "-"}
                </span>
                <span>
                  <i className="bi bi-person meta-icon"></i> {entry.createdByName || "User"}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* BOTTOM BUTTONS */}
      {canAddEntry && (
        <div className="bd-bottom-actions">
          <button className="bd-btn-in" onClick={() => setShowEntryModal("IN")}>
            + Cash In
          </button>
          <button className="bd-btn-out" onClick={() => setShowEntryModal("OUT")}>
            - Cash Out
          </button>
        </div>
      )}

      {/* MODALS */}
      {showRenameModal && (
        <RenameBookModal
          currentName={book.name}
          onClose={() => setShowRenameModal(false)}
          onRename={handleRename}
        />
      )}

      {showEntryModal && (
        <AddEntryModal
          type={showEntryModal}
          onClose={() => setShowEntryModal(null)}
          onCreate={handleCreateEntry}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          open={showDeleteConfirm}
          title="Delete Book?"
          message="This will permanently delete all entries inside this book."
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={() => {
            setShowDeleteConfirm(false);
            handleDeleteBook();
          }}
        />
      )}

      {showMemberModal && (
        <AddMemberModal
          onClose={() => setShowMemberModal(false)}
          onSubmit={handleAddMember}
        />
      )}
    </div>
  );
};

export default BookDetail;
